import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import GridViewIcon from "@mui/icons-material/GridView";
import StarIcon from "@mui/icons-material/Star";
import logo from "../assets/images/logo512.png";
import SearchBar from "./SearchBar";

const HEADER_HEIGHT = 56 + 38; // topbar + nav row

const HeaderBar = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: HEADER_HEIGHT,
  zIndex: 1400,
  display: "flex",
  alignItems: "center",
  background: "#fff",
  borderBottom: "1px solid #eee",
  padding: "0 12px",
  gap: 12,
});

const LoginBtn = styled(Button)({
  border: "2px solid #FF4196",
  borderRadius: "50px",
  color: "#FF4196",
  fontWeight: 700,
  fontSize: "0.78rem",
  textTransform: "uppercase",
  padding: "5px 18px",
  whiteSpace: "nowrap",
  "&:hover": {
    background: "rgba(255,65,150,0.06)",
    borderColor: "#FF4196",
  },
});

const SignupBtn = styled(Button)({
  background: "linear-gradient(135deg, #FF4196, #e91e63)",
  borderRadius: "50px",
  color: "#fff",
  fontWeight: 700,
  fontSize: "0.78rem",
  textTransform: "uppercase",
  padding: "5px 18px",
  whiteSpace: "nowrap",
  "&:hover": {
    background: "linear-gradient(135deg, #e91e63, #c2185b)",
  },
});

// ─── Nav items ───────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { label: "Live Cams",   to: "/",              icon: <LiveTvIcon sx={{ fontSize: 16 }} /> },
  { label: "Videos",      to: "/videos",         icon: <VideoLibraryIcon sx={{ fontSize: 16 }} /> },
  { label: "Categories",  to: "/category/latina-cam-girls", icon: <GridViewIcon sx={{ fontSize: 16 }} /> },
  { label: "Top Models",  to: "/top-cam-girls",  icon: <StarIcon sx={{ fontSize: 16 }} /> },
];

const NavBar = styled(Box)({
  position: "fixed",
  top: 56,
  left: 0,
  right: 0,
  height: 38,
  zIndex: 1399,
  display: "flex",
  alignItems: "center",
  background: "#fff",
  borderBottom: "1px solid #f0f0f0",
  padding: "0 12px",
  gap: 4,
  overflowX: "auto",
  "&::-webkit-scrollbar": { display: "none" },
});

const NavItem = styled(Box)(({ active }) => ({
  display: "flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 14px",
  borderRadius: 20,
  fontSize: "0.78rem",
  fontWeight: active ? 700 : 500,
  color: active ? "#fff" : "#444",
  background: active ? "linear-gradient(135deg,#FF4196,#e91e63)" : "#f5f5f5",
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
  textDecoration: "none",
  transition: "all 0.15s",
  "&:hover": {
    background: active ? "linear-gradient(135deg,#e91e63,#c2185b)" : "#ebebeb",
    color: active ? "#fff" : "#111",
  },
}));

const Header = ({ onMenuToggle }) => {
  const location = useLocation();

  return (
    <>
    <HeaderBar>
      {/* Hamburger — visible on mobile/tablet */}
      <IconButton
        onClick={onMenuToggle}
        sx={{ display: { xs: "flex", lg: "none" }, p: 0.5 }}
      >
        <MenuIcon sx={{ fontSize: 26, color: "#333" }} />
      </IconButton>

      {/* Logo */}
      <Box
        component="a"
        href="/"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          textDecoration: "none",
          flexShrink: 0,
        }}
      >
        <img
          src={logo}
          alt="InstaCamGirls"
          style={{ width: 32, height: 32, objectFit: "contain" }}
        />
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "0.85rem", md: "1rem" },
            color: "#111",
            letterSpacing: 0.3,
            display: { xs: "none", sm: "block" },
          }}
        >
          InstaCamGirls
        </Typography>
      </Box>

      {/* Search bar — center, expands to fill */}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", mx: { xs: 0.5, md: 2 } }}>
        <SearchBar />
      </Box>

      {/* Right buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {/* Mobile: icon only */}
        <IconButton
          href="/join"
          sx={{
            display: { xs: "flex", md: "none" },
            color: "#FF4196",
            border: "2px solid #FF4196",
            borderRadius: "50%",
            p: 0.5,
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Desktop: full buttons */}
        <LoginBtn
          variant="outlined"
          href="/join"
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          Login
        </LoginBtn>
        <SignupBtn
          variant="contained"
          href="/join"
          disableElevation
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          Create Account
        </SignupBtn>
      </Box>
    </HeaderBar>

    {/* ── Navigation row ── */}
    <NavBar>
      {NAV_ITEMS.map((item) => {
        const active = item.to === "/"
          ? location.pathname === "/"
          : location.pathname.startsWith(item.to);
        return (
          <NavItem
            key={item.to}
            component={Link}
            to={item.to}
            active={active ? 1 : 0}
          >
            {item.icon}
            {item.label}
          </NavItem>
        );
      })}
    </NavBar>
    </>
  );
};

export { HEADER_HEIGHT };
export default Header;
